/**
 * Encrypted local storage for app passwords + signup metadata.
 * AES-GCM with a key derived from a user-supplied passphrase via PBKDF2.
 * Stored in a dedicated IndexedDB row; the passphrase itself never leaves
 * the browser and is not persisted.
 */

import Dexie, { type Table } from 'dexie'

export type VaultEntry = {
  handle: string
  email?: string
  appPassword: string
  did?: string
  notes?: string
  createdAt: number
}

type StoredBlob = {
  id: 'vault'
  salt: string // base64
  iv: string // base64
  ciphertext: string // base64
  updatedAt: number
}

class VaultDB extends Dexie {
  blobs!: Table<StoredBlob, string>

  constructor() {
    super('bskydisc.vault')
    this.version(1).stores({ blobs: 'id, updatedAt' })
  }
}

const vdb = new VaultDB()

const enc = new TextEncoder()
const dec = new TextDecoder()

function b64(bytes: Uint8Array): string {
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s)
}
function fromB64(s: string): Uint8Array {
  const bin = atob(s)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase) as BufferSource,
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  )
  return await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: 250_000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

export async function loadVault(passphrase: string): Promise<VaultEntry[]> {
  const blob = await vdb.blobs.get('vault')
  if (!blob) return []
  const salt = fromB64(blob.salt)
  const iv = fromB64(blob.iv)
  const key = await deriveKey(passphrase, salt)
  try {
    const ct = fromB64(blob.ciphertext)
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      ct as BufferSource,
    )
    const text = dec.decode(plain)
    return JSON.parse(text) as VaultEntry[]
  } catch {
    throw new Error('wrong passphrase or corrupted vault')
  }
}

export async function saveVault(passphrase: string, entries: VaultEntry[]): Promise<void> {
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await deriveKey(passphrase, salt)
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      enc.encode(JSON.stringify(entries)) as BufferSource,
    ),
  )
  const blob: StoredBlob = {
    id: 'vault',
    salt: b64(salt),
    iv: b64(iv),
    ciphertext: b64(ciphertext),
    updatedAt: Date.now(),
  }
  await vdb.blobs.put(blob)
}

export async function vaultExists(): Promise<boolean> {
  return (await vdb.blobs.get('vault')) != null
}

export async function clearVault(): Promise<void> {
  await vdb.blobs.delete('vault')
}
